import React from "react";
import { Table, Tag, Tooltip, Popconfirm } from "antd";
import { FiEdit2, FiEye, FiTrash2 } from "react-icons/fi";
import { Link } from "react-router-dom";
import moment from "moment";
import TableSkeleton from "../skeleton/TableSkeleton";

const ProductGroupsTable = ({ data, pagination, loading, onChange, onEdit, onDelete, rowSelection }) => {
    if (loading && (!data || data.length === 0)) {
        return <TableSkeleton columns={6} />;
    }

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            sorter: true,
            render: (text) => <span className="fw-bold">{text}</span>
        },
        {
            title: "Slug",
            dataIndex: "slug",
            key: "slug",
            render: (text) => text ? <span>{text}</span> : <span className="text-muted">N/A</span>
        },
        {
            title: "Group Type",
            dataIndex: "groupType",
            key: "groupType",
            render: (text, record) => {
                const rawType = String(text || record?.type || record?.group_type || "").toLowerCase().trim();
                const isRelated = rawType === "related" || rawType === "related_products" || rawType === "related_group";
                const isVariant = rawType === "variant" || rawType === "variant_group" || rawType === "variants" || (!isRelated && rawType !== "bundle" && rawType !== "");
                const color = isRelated ? "green" : (isVariant ? "blue" : "default");
                const label = isRelated ? "RELATED" : (isVariant ? "VARIANT" : (rawType.toUpperCase() || "N/A"));
                return (
                    <Tag color={color}>
                        {label}
                    </Tag>
                );
            }
        },
        {
            title: "Description",
            dataIndex: "description",
            key: "description",
            render: (text) => text ? <span>{text.length > 50 ? text.substring(0, 50) + '...' : text}</span> : <span className="text-muted">N/A</span>
        },
        {
            title: "Date Created",
            dataIndex: "date_created",
            key: "date_created",
            sorter: true,
            render: (text) => text ? moment(text).format("MMM DD, YYYY hh:mm A") : "N/A"
        },
        {
            title: "Updated At",
            dataIndex: "updated_at",
            key: "updated_at",
            render: (text) => text ? moment(text).format("MMM DD, YYYY hh:mm A") : "N/A"
        },
        {
            title: "Action",
            key: "action",
            render: (text, record) => (
                <div className="d-flex align-items-center gap-2">
                    <Link to={`/admin/product-group-details/${record._id || record.groupId || record.id}`}>
                        <Tooltip title="View Details">
                            <button 
                                className="btn btn-sm btn-light shadow-sm d-flex align-items-center justify-content-center"
                                style={{ width: "32px", height: "32px", borderRadius: "8px" }}
                            >
                                <FiEye className="text-success" />
                            </button>
                        </Tooltip>
                    </Link>
                    <Tooltip title="Edit Group">
                        <button 
                            className="btn btn-sm btn-light shadow-sm d-flex align-items-center justify-content-center"
                            style={{ width: "32px", height: "32px", borderRadius: "8px" }}
                            onClick={(e) => { e.stopPropagation(); if (onEdit) onEdit(record); }}
                        >
                            <FiEdit2 className="text-primary" />
                        </button>
                    </Tooltip>
                    <Popconfirm
                        title="Delete Product Group"
                        description={`Are you sure you want to delete "${record.name || 'this group'}"?`}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                        onConfirm={(e) => {
                            e?.stopPropagation?.();
                            const targetId = record._id || record.groupId || record.id;
                            if (onDelete && targetId) onDelete(targetId);
                        }}
                    >
                        <Tooltip title="Delete Group">
                            <button 
                                className="btn btn-sm btn-light shadow-sm d-flex align-items-center justify-content-center text-danger"
                                style={{ width: "32px", height: "32px", borderRadius: "8px" }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <FiTrash2 />
                            </button>
                        </Tooltip>
                    </Popconfirm>
                </div>
            )
        }
    ];

    return (
        <div className="table-responsive">
            <Table
                columns={columns}
                dataSource={data}
                rowKey={(record) => record._id || record.groupId || record.id || record.slug}
                rowSelection={rowSelection}
                pagination={{
                    current: pagination.currentPage,
                    pageSize: pagination.pageSize,
                    total: pagination.totalItems,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '25', '50', '100'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} groups`
                }}
                onChange={onChange}
                loading={loading}
            />
        </div>
    );
};

export default ProductGroupsTable;
